import React, { useEffect, useState } from "react";
import { ScrollText, Loader2 } from "lucide-react";
import api from "../lib/api";

const ACTION_LABELS = {
    client_profile_viewed: "Viewed client submission",
    photos_zip_downloaded: "Downloaded photos ZIP",
    login_link_generated: "Generated login link",
    webhook_secret_viewed: "Viewed webhook secret",
    webhook_secret_rotated: "Rotated webhook secret",
    tenant_soft_deleted: "Moved client to trash",
    tenant_restored: "Restored client",
    tenant_provisioned: "Provisioned client",
    mfa_enabled: "Enabled 2FA on own account",
    mfa_disabled: "Disabled 2FA on own account",
};

export default function AdminAudit() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/admin/audit", { params: { limit: 200 } })
            .then((r) => setRows(r.data))
            .catch(() => setError("Couldn't load the audit log."));
    }, []);

    return (
        <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12" data-testid="admin-audit-page">
            <p className="text-[11.5px] uppercase tracking-[0.18em] text-zinc-500">WebJesters · Admin</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50 flex items-center gap-3">
                <ScrollText className="h-7 w-7 text-zinc-400 stroke-[1.5]" /> Audit log
            </h1>
            <p className="mt-2 text-[14px] text-zinc-400 max-w-2xl">
                Every privileged action on client data — who did it, to which client, from where. Retained for incident
                response and breach-notification timelines.
            </p>

            <div className="mt-8 rounded-xl border border-zinc-800/80 overflow-hidden">
                {rows == null && !error && (
                    <div className="p-8 flex items-center gap-2 text-zinc-500 text-sm" data-testid="admin-audit-loading">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading
                    </div>
                )}
                {error && <div className="p-8 text-sm text-red-400" data-testid="admin-audit-error">{error}</div>}
                {rows && rows.length === 0 && (
                    <div className="p-8 text-sm text-zinc-500" data-testid="admin-audit-empty">No privileged actions recorded yet.</div>
                )}
                {rows && rows.length > 0 && (
                    <table className="w-full text-[13px]" data-testid="admin-audit-table">
                        <thead className="bg-zinc-950/60 text-zinc-500 text-left text-[11.5px] uppercase tracking-wider">
                            <tr>
                                <th className="px-4 py-3 font-medium">When</th>
                                <th className="px-4 py-3 font-medium">Action</th>
                                <th className="px-4 py-3 font-medium">Client</th>
                                <th className="px-4 py-3 font-medium hidden md:table-cell">Admin</th>
                                <th className="px-4 py-3 font-medium hidden lg:table-cell">IP</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-900">
                            {rows.map((r) => (
                                <tr key={r.id} className="hover:bg-zinc-900/40" data-testid={`audit-row-${r.id}`}>
                                    <td className="px-4 py-3 text-zinc-400 whitespace-nowrap tabular-nums">
                                        {new Date(r.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-zinc-100">
                                        {ACTION_LABELS[r.action] || r.action}
                                        {r.meta?.target && <span className="text-zinc-500"> · {r.meta.target}</span>}
                                    </td>
                                    <td className="px-4 py-3 text-zinc-300">{r.business_name || <span className="text-zinc-600">—</span>}</td>
                                    <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{r.actor_email}</td>
                                    <td className="px-4 py-3 text-zinc-500 font-mono text-[12px] hidden lg:table-cell">{r.ip}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
